"use client";

import { useRef, useState, useCallback, useEffect } from "react";

type Tool = "draw" | "highlight" | "blur";

export interface ScreenshotAnnotatorProps {
  imageData: string;
  onSave: (annotatedData: string) => void;
  onCancel: () => void;
  /** Optional overrides for the small toolbar buttons. */
  labels?: {
    draw?: string;
    highlight?: string;
    blur?: string;
    undo?: string;
    save?: string;
    cancel?: string;
  };
}

const TOOL_CONFIG: Record<Tool, { color: string; cursor: string }> = {
  draw: { color: "#ef4444", cursor: "crosshair" },
  highlight: { color: "rgba(250, 204, 21, 0.4)", cursor: "crosshair" },
  blur: { color: "rgba(0,0,0,0)", cursor: "cell" },
};

/**
 * Lightweight in-place screenshot annotator. Pure UI — no network, no auth.
 * Moved into @pontimed/feedback-ui so apps/web and apps/app share one
 * implementation. Uses canvas + raw pointer events; no extra deps.
 */
export function ScreenshotAnnotator({
  imageData,
  onSave,
  onCancel,
  labels,
}: ScreenshotAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>("draw");
  const [drawing, setDrawing] = useState(false);
  const [paths, setPaths] = useState<
    Array<{ tool: Tool; points: Array<{ x: number; y: number }> }>
  >([]);
  const currentPath = useRef<Array<{ x: number; y: number }>>([]);

  const drawLabel = labels?.draw ?? "Draw";
  const highlightLabel = labels?.highlight ?? "Highlight";
  const blurLabel = labels?.blur ?? "Blur";
  const undoLabel = labels?.undo ?? "Undo";
  const saveLabel = labels?.save ?? "Save";
  const cancelLabel = labels?.cancel ?? "Cancel";

  const getScale = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return 1;
    return canvas.width / img.width;
  }, []);

  const redraw = useCallback(
    (canvas: HTMLCanvasElement, img: HTMLImageElement, allPaths: typeof paths, _scale: number) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      for (const p of allPaths) {
        if (p.points.length < 2) continue;

        if (p.tool === "blur") {
          const minX = Math.min(...p.points.map((pt) => pt.x));
          const maxX = Math.max(...p.points.map((pt) => pt.x));
          const minY = Math.min(...p.points.map((pt) => pt.y));
          const maxY = Math.max(...p.points.map((pt) => pt.y));
          const w = maxX - minX;
          const h = maxY - minY;
          if (w > 4 && h > 4) {
            const pixelSize = 8;
            const imgData = ctx.getImageData(minX, minY, w, h);
            for (let py = 0; py < h; py += pixelSize) {
              for (let px = 0; px < w; px += pixelSize) {
                const i = (py * w + px) * 4;
                const r = imgData.data[i];
                const g = imgData.data[i + 1];
                const b = imgData.data[i + 2];
                for (let dy = 0; dy < pixelSize && py + dy < h; dy++) {
                  for (let dx = 0; dx < pixelSize && px + dx < w; dx++) {
                    const j = ((py + dy) * w + (px + dx)) * 4;
                    imgData.data[j] = r;
                    imgData.data[j + 1] = g;
                    imgData.data[j + 2] = b;
                  }
                }
              }
            }
            ctx.putImageData(imgData, minX, minY);
          }
        } else {
          ctx.beginPath();
          ctx.strokeStyle = TOOL_CONFIG[p.tool].color;
          ctx.lineWidth = p.tool === "highlight" ? 20 : 3;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.moveTo(p.points[0].x, p.points[0].y);
          for (let i = 1; i < p.points.length; i++) {
            ctx.lineTo(p.points[i].x, p.points[i].y);
          }
          ctx.stroke();
        }
      }
    },
    [],
  );

  // Declared AFTER `redraw` on purpose: this effect calls it, and reading a
  // `const` from above its declaration keeps the React Compiler from bailing
  // out early (it cannot prove the binding is initialised). The load handler
  // is async so it worked at runtime, but the ordering is the honest fix.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const maxW = Math.min(600, window.innerWidth - 32);
      const scale = maxW / img.width;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      redraw(canvas, img, paths, scale);
    };
    img.src = imageData;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-running on `paths` would reload the image on every stroke; `redraw` is stable.
  }, [imageData]);

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setDrawing(true);
      currentPath.current = [getPos(e)];
    },
    [getPos],
  );

  const moveDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!drawing) return;
      e.preventDefault();
      const pos = getPos(e);
      currentPath.current.push(pos);

      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (canvas && img) {
        const allPaths = [...paths, { tool, points: currentPath.current }];
        redraw(canvas, img, allPaths, getScale());
      }
    },
    [drawing, getPos, paths, tool, redraw, getScale],
  );

  const endDraw = useCallback(() => {
    if (!drawing) return;
    setDrawing(false);
    if (currentPath.current.length > 1) {
      setPaths((prev) => [...prev, { tool, points: [...currentPath.current] }]);
    }
    currentPath.current = [];
  }, [drawing, tool]);

  const undo = useCallback(() => {
    setPaths((prev) => {
      const next = prev.slice(0, -1);
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (canvas && img) redraw(canvas, img, next, getScale());
      return next;
    });
  }, [redraw, getScale]);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL("image/jpeg", 0.8));
  }, [onSave]);

  const toolLabel: Record<Tool, string> = {
    draw: drawLabel,
    highlight: highlightLabel,
    blur: blurLabel,
  };

  return (
    <div className="fixed inset-0 z-[3500] flex flex-col items-center justify-center bg-black/80 p-4">
      <div className="bg-card mb-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 shadow-lg">
        {(Object.keys(TOOL_CONFIG) as Tool[]).map((toolKey) => (
          <button
            key={toolKey}
            type="button"
            onClick={() => setTool(toolKey)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              tool === toolKey
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {toolLabel[toolKey]}
          </button>
        ))}
        <div className="bg-border mx-1 h-4 w-px" />
        <button
          type="button"
          onClick={undo}
          disabled={paths.length === 0}
          className="text-muted-foreground hover:bg-muted rounded-md px-2 py-1 text-xs disabled:opacity-30"
        >
          {undoLabel}
        </button>
        <div className="bg-border mx-1 h-4 w-px" />
        <button
          type="button"
          onClick={handleSave}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 py-1 text-xs font-medium"
        >
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:bg-muted rounded-md px-2 py-1 text-xs"
        >
          {cancelLabel}
        </button>
      </div>

      <canvas
        ref={canvasRef}
        className="max-h-[75vh] max-w-full rounded-lg shadow-2xl"
        style={{ cursor: TOOL_CONFIG[tool].cursor, touchAction: "none" }}
        onMouseDown={startDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={moveDraw}
        onTouchEnd={endDraw}
      />
    </div>
  );
}
